import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';

// ========================================
// Styled Components
// ========================================

const Container = styled.div`
  max-width: 600px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #00a6c2;
  margin: 0 0 24px 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 24px 0;
`;

const RequirementsSection = styled.div`
  margin-bottom: 24px;
`;

const RequirementsTitle = styled.div`
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
`;

const RequirementsList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
`;

const RequirementItem = styled.li`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const FormSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
  
  &::after {
    content: ' *';
    color: #ef4444;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 14px;
  margin: 8px 0 0 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  ${({ $variant = 'secondary' }) =>
    $variant === 'primary'
      ? 'background: #3b82f6; color: white; &:hover { background: #2563eb; }'
      : 'background: #e5e7eb; color: #374151; &:hover { background: #d1d5db; }'}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelLink = styled(Link)`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  background: #e5e7eb;
  color: #374151;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: background 0.2s;

  &:hover {
    background: #d1d5db;
  }
`;

const Toast = styled.div<{ $type: 'success' | 'error' }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  animation: slideIn 0.3s ease;

  ${({ $type }) =>
    $type === 'success' ? 'background: #10b981; color: white;' : 'background: #ef4444; color: white;'}

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 48px;
  color: #ef4444;
`;

// Mirrors User::PASSWORD_SPECIAL_CHARACTERS in contently-contently.
const PASSWORD_SPECIAL_CHARACTERS = "!#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";

// ========================================
// Component
// ========================================

interface UserBasicInfo {
  id: number;
  fullName: string;
  email: string;
}

function AdminUserPasswordPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserBasicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchUser = useCallback(async (userId: string) => {
    try {
      const response = await api.get(`/admin2/users/${userId}.json`);
      const userData = response.data.user || response.data; // Handle both nested and flat response
      setUser({
        id: userData.id,
        fullName: userData.fullName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        email: userData.email,
      });
    } catch (err: any) {
      setError(err.response?.data?.errors?.join(', ') || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchUser(id);
    }
  }, [id, fetchUser]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setPasswordError(null);
    setConfirmationError(null);

    // Validate
    let hasError = false;

    if (!password) {
      setPasswordError("Password can't be blank");
      hasError = true;
    }

    if (password !== passwordConfirmation) {
      setConfirmationError("Password confirmation doesn't match Password");
      hasError = true;
    }

    if (hasError) return;

    setSubmitting(true);
    try {
      const payload = {
        user: {
          password,
          password_confirmation: passwordConfirmation,
        },
      };
      const response = await api.put(`/admin2/users/${id}/password.json`, payload);
      if (response.data?.success) {
        showToast('Password changed successfully', 'success');
        // Redirect back to user edit page after short delay
        setTimeout(() => {
          navigate(`/admin2/users/${id}/edit`);
        }, 1500);
      } else {
        const errors = response.data?.errors || ['Failed to update password'];
        // Try to map errors to specific fields
        errors.forEach((err: string) => {
          if (err.toLowerCase().includes('confirmation')) {
            setConfirmationError(err);
          } else {
            setPasswordError(err);
          }
        });
      }
    } catch (err: any) {
      const errors = err.response?.data?.errors || ['Failed to update password'];
      if (Array.isArray(errors)) {
        errors.forEach((errMsg: string) => {
          if (errMsg.toLowerCase().includes('confirmation')) {
            setConfirmationError(errMsg);
          } else {
            setPasswordError(errMsg);
          }
        });
      } else {
        setPasswordError(errors);
      }
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <AdminLayout title="Password" breadcrumb={[{ label: 'Users', href: '/admin2/users' }]}>
        <LoadingState>Loading user...</LoadingState>
      </AdminLayout>
    );
  }

  if (error || !user) {
    return (
      <AdminLayout title="Password" breadcrumb={[{ label: 'Users', href: '/admin2/users' }]}>
        <ErrorState>User not found or you don't have permission to view this user.</ErrorState>
      </AdminLayout>
    );
  }

  const displayName = user.fullName || user.email;

  return (
    <AdminLayout
      title="Password"
      breadcrumb={[
        { label: 'Users', href: '/admin2/users' },
        { label: `${displayName}${user.email ? ` (${user.email})` : ''}`, href: `/admin2/users/${id}/edit` },
        { label: 'Password' },
      ]}
    >
      <Container>
        <Title>Change password for {displayName}</Title>
        
        <Divider />
        
        <RequirementsSection>
          <RequirementsTitle>Password requirements:</RequirementsTitle>
          <RequirementsList>
            <RequirementItem>8 or more characters</RequirementItem>
            <RequirementItem>At least one number and one letter</RequirementItem>
            <RequirementItem>
              Should contain one special characters such as {PASSWORD_SPECIAL_CHARACTERS.split('').join(' ')}
            </RequirementItem>
          </RequirementsList>
        </RequirementsSection>
        
        <Divider />
        
        <FormSection>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              {passwordError && <ErrorText>{passwordError}</ErrorText>}
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="passwordConfirmation">Password confirmation</Label>
              <Input
                id="passwordConfirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                autoComplete="new-password"
                required
              />
              {confirmationError && <ErrorText>{confirmationError}</ErrorText>}
            </FormGroup>
            
            <ButtonGroup>
              <CancelLink to="/admin2/users">Cancel</CancelLink>
              <Button type="submit" $variant="primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </ButtonGroup>
          </form>
        </FormSection>
      </Container>

      {toast && <Toast $type={toast.type}>{toast.message}</Toast>}
    </AdminLayout>
  );
}

export default AdminUserPasswordPage;
